import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  TextField,
  Rating,
  Stack,
  Avatar,
  IconButton,
  CircularProgress,
  useTheme,
  alpha,
  Divider,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import { useSnackbar } from "notistack";

import { Strings, RequestStatus } from "@app/constants";
import { addTestimonial, updateTestimonial } from "../slice";
import {
  selectAddTestimonialState,
  selectUpdateTestimonialState,
} from "../selector";
import { ITestimonial, ITestimonialPayload } from "../types";

interface AddEditTestimonialModalProps {
  open: boolean;
  onClose: () => void;
  selectedTestimonialData: ITestimonial | null;
}

const AddEditTestimonialModal: React.FC<AddEditTestimonialModalProps> = ({
  open,
  onClose,
  selectedTestimonialData,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isEdit = Boolean(selectedTestimonialData?.id);

  const addState = useSelector(selectAddTestimonialState);
  const updateState = useSelector(selectUpdateTestimonialState);

  const isSubmitting =
    addState?.status === RequestStatus.RUNNING ||
    updateState?.status === RequestStatus.RUNNING;

  // Form Fields
  const [reviewerName, setReviewerName] = useState<string>("");
  const [rating, setRating] = useState<number>(5);
  const [comment, setComment] = useState<string>("");
  const [verifiedBuyer, setVerifiedBuyer] = useState<boolean>(true);
  const [tag, setTag] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (open) {
      if (selectedTestimonialData) {
        setReviewerName(
          selectedTestimonialData.reviewerName ||
            selectedTestimonialData.name ||
            selectedTestimonialData.author ||
            ""
        );
        setRating(
          Number(
            selectedTestimonialData.rating ||
              selectedTestimonialData.stars ||
              5
          )
        );
        setComment(
          selectedTestimonialData.comment ||
            selectedTestimonialData.review ||
            selectedTestimonialData.message ||
            ""
        );
        setVerifiedBuyer(
          selectedTestimonialData.verifiedBuyer !== undefined
            ? Boolean(selectedTestimonialData.verifiedBuyer)
            : true
        );
        setTag(selectedTestimonialData.tag || "");

        const img =
          selectedTestimonialData.image?.url ||
          (typeof selectedTestimonialData.image === "string"
            ? selectedTestimonialData.image
            : "") ||
          selectedTestimonialData.avatar ||
          "";
        setImagePreview(img);
        setImageFile(null);
      } else {
        setReviewerName("");
        setRating(5);
        setComment("");
        setVerifiedBuyer(true);
        setTag("");
        setImageFile(null);
        setImagePreview("");
      }
      setErrors({});
    }
  }, [open, selectedTestimonialData]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validate = () => {
    const errs: { [key: string]: string } = {};
    if (!reviewerName.trim()) {
      errs.reviewerName = "Reviewer Name is required";
    }
    if (!rating || rating < 1) {
      errs.rating = "Rating is required (at least 1 star)";
    }
    if (!comment.trim()) {
      errs.comment = "Comment is required";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload: ITestimonialPayload = {
      reviewerName: reviewerName.trim(),
      rating: Number(rating),
      comment: comment.trim(),
      verifiedBuyer: Boolean(verifiedBuyer),
      tag: tag.trim() || undefined,
      image: imageFile || (imagePreview ? imagePreview : null),
    };

    if (isEdit && selectedTestimonialData?.id) {
      dispatch(
        updateTestimonial({
          id: selectedTestimonialData.id,
          data: payload,
        })
      );
      enqueueSnackbar("Review updated successfully", { variant: "success" });
    } else {
      dispatch(addTestimonial(payload));
      enqueueSnackbar("Review added successfully", { variant: "success" });
    }

    onClose();
  };

  const fontFamily = Strings.fontLanguage.workSans || "'Work Sans', sans-serif";

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              display: "flex",
            }}
          >
            <Icon
              icon={
                isEdit
                  ? "solar:pen-new-square-linear"
                  : "solar:chat-round-like-linear"
              }
              width={24}
              height={24}
            />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontFamily }}>
            {isEdit ? "Edit Review" : "Add New Review"}
          </Typography>
        </Stack>

        <IconButton onClick={onClose} size="small" disabled={isSubmitting}>
          <Icon icon="mdi:close" width={20} />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 3 }}>
        <Stack spacing={2.5}>
          {/* Reviewer Name (Required) */}
          <TextField
            fullWidth
            required
            label="Reviewer Name *"
            placeholder="e.g. John Doe"
            value={reviewerName}
            onChange={(e) => setReviewerName(e.target.value)}
            error={Boolean(errors.reviewerName)}
            helperText={errors.reviewerName}
            size="small"
          />

          {/* Rating (Required) */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 0.5 }}
            >
              Rating *
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Rating
                name="rating"
                value={rating}
                onChange={(_, newValue) => {
                  if (newValue) setRating(newValue);
                }}
                size="large"
                sx={{
                  color: "#faaf00",
                  "& .MuiRating-iconFilled": {
                    color: "#faaf00",
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.secondary,
                }}
              >
                ({rating} Star{rating > 1 ? "s" : ""})
              </Typography>
            </Stack>
            {errors.rating && (
              <Typography variant="caption" color="error">
                {errors.rating}
              </Typography>
            )}
          </Box>

          {/* Comment (Required) */}
          <TextField
            fullWidth
            required
            label="Comment *"
            placeholder="Write the customer's review comment..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            error={Boolean(errors.comment)}
            helperText={errors.comment}
            multiline
            rows={4}
          />

          {/* Verified Buyer (Required / Boolean Toggle) */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: alpha(theme.palette.success.main, 0.04),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Verified Buyer *
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {verifiedBuyer
                  ? "Marked as a verified customer purchase"
                  : "Not marked as verified buyer"}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={verifiedBuyer}
                  onChange={(e) => setVerifiedBuyer(e.target.checked)}
                  color="success"
                />
              }
              label={verifiedBuyer ? "Yes" : "No"}
              sx={{ m: 0 }}
            />
          </Box>

          {/* Tag (Optional - Not Required) */}
          <TextField
            fullWidth
            label="Tag (Optional)"
            placeholder="e.g. Car Buyer, Smooth Process, Excellent Service"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            size="small"
            helperText="Optional label or category for this review"
          />

          {/* Image / Avatar Upload (Optional - Not Required) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.grey[50],
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Avatar
              src={imagePreview}
              alt="Preview"
              sx={{
                width: 60,
                height: 60,
                bgcolor: theme.palette.primary.main,
                fontSize: "1.3rem",
                fontWeight: 700,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              {reviewerName ? reviewerName.charAt(0).toUpperCase() : "U"}
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Reviewer Photo / Image (Optional)
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Optional customer avatar or car delivery photo
              </Typography>

              <Stack direction="row" spacing={1} mt={1}>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleImageChange}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  startIcon={<Icon icon="solar:camera-outline" width={16} />}
                  sx={{
                    textTransform: "none",
                    borderRadius: 1,
                    fontSize: "0.8rem",
                    borderColor: theme.palette.divider,
                    color: "text.primary",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  {imagePreview ? "Change Image" : "Upload Image"}
                </Button>

                {imagePreview && (
                  <Button
                    variant="text"
                    size="small"
                    color="error"
                    onClick={handleRemoveImage}
                    startIcon={
                      <Icon icon="solar:trash-bin-trash-outline" width={16} />
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.8rem",
                    }}
                  >
                    Remove
                  </Button>
                )}
              </Stack>
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            textTransform: "none",
            fontWeight: 600,
            fontFamily,
            px: 2.5,
            "&:hover": {
              borderColor: theme.palette.primary.dark,
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
            },
          }}
        >
          {Strings.button.cancel}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{
            borderRadius: 1,
            backgroundColor: theme.palette.primary.main,
            color: "white",
            textTransform: "none",
            fontFamily,
            fontWeight: 600,
            px: 3,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={20} sx={{ color: "white" }} />
          ) : isEdit ? (
            "Update Review"
          ) : (
            "Add Review"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { AddEditTestimonialModal };
